import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Upload, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg, downloadCsv } from "@/lib/api";
import { num, periodLabel } from "@/lib/format";

const Status = ({ r, newLabel }) =>
  r.error ? <span className="text-rose-700 text-xs" data-testid="import-row-error">{r.error}</span>
    : <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm ${newLabel ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-700"}`}>{newLabel ? "BARU" : "UBAH"}</span>;

function CustomerPreview({ res }) {
  return (
    <table className="xl">
      <thead><tr><th className="rownum">Baris</th><th>Nama</th><th>Telepon</th><th>Alamat</th><th>Detail</th><th>Status</th></tr></thead>
      <tbody>{res.rows.map((r) => (
        <tr key={r.row}><td className="rownum">{r.row}</td><td>{r.name}</td><td className="font-mono text-xs">{r.phone}</td><td>{r.address}</td><td className="text-xs">{r.address_detail}</td><td><Status r={r} newLabel={r.action === "baru"} /></td></tr>
      ))}</tbody>
    </table>
  );
}

function RecapPreview({ res }) {
  return (
    <table className="xl">
      <thead><tr><th className="rownum">Baris</th><th>Pelanggan</th>{res.columns.map((c) => <th key={c} className="text-right">{c}</th>)}<th>Status</th></tr></thead>
      <tbody>{res.rows.map((r) => (
        <tr key={r.row}><td className="rownum">{r.row}</td><td>{r.customer}</td>{res.columns.map((c) => <td key={c} className="num">{r.values[c] ?? ""}</td>)}<td><Status r={r} newLabel={r.is_new} /></td></tr>
      ))}</tbody>
    </table>
  );
}

function Summary({ kind, res }) {
  const s = res.summary;
  return (
    <div className="flex flex-wrap gap-2 text-xs" data-testid="import-summary">
      {kind === "customers" ? (
        <>
          <span className="px-2 py-1 bg-sky-50 border border-sky-200 rounded-sm">Baru: <b>{s.baru}</b></span>
          <span className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-sm">Diperbarui: <b>{s.ubah}</b></span>
          {s.alamat_baru.length > 0 && <span className="px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-sm">Alamat baru: <b>{s.alamat_baru.join(", ")}</b></span>}
        </>
      ) : (
        <>
          <span className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-sm">Baris valid: <b>{s.baris}</b></span>
          <span className="px-2 py-1 bg-sky-50 border border-sky-200 rounded-sm">Pelanggan baru: <b>{s.pelanggan_baru}</b></span>
          <span className="px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-sm">Total qty: <b>{num(s.total_qty)}</b></span>
          {res.ignored_columns.length > 0 && <span className="px-2 py-1 bg-amber-50 border border-amber-200 rounded-sm">Kolom diabaikan: {res.ignored_columns.join(", ")}</span>}
        </>
      )}
      {s.error > 0 && <span className="px-2 py-1 bg-rose-50 border border-rose-200 text-rose-800 rounded-sm">Error (dilewati): <b>{s.error}</b></span>}
    </div>
  );
}

export function ImportDialog({ kind, period, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const isRecap = kind === "recap";
  const params = isRecap ? { period } : {};
  const okCount = res ? (isRecap ? res.summary.baris : res.summary.baru + res.summary.ubah) : 0;
  const reset = () => { setFile(null); setRes(null); };
  const run = async (commit, f = file) => {
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post(`/import/${kind}`, fd, { params: { ...params, commit } });
      setRes(data);
      if (commit) {
        toast.success(`Impor berhasil: ${okCount} baris`);
        setOpen(false);
        reset();
        onDone?.();
      }
    } catch (e) {
      toast.error(errMsg(e));
      if (!commit) setRes(null);
    } finally { setBusy(false); }
  };
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) { setFile(f); run(false, f); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid={`${kind}-import-btn`}><FileSpreadsheet className="h-4 w-4 mr-2" />Impor Excel</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle>{isRecap ? `Impor Rekap Penjualan · ${periodLabel(period)}` : "Impor Data Pelanggan"}</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-600 space-y-1">
          <p>Format file: <b>.xlsx, .xls, atau .csv</b>. Baris judul harus memiliki kolom <b>{isRecap ? "Pelanggan" : "Nama"}</b>.</p>
          {isRecap
            ? <p>Kolom lain dicocokkan dengan nama/kode jenis voucher (mis. "Voucher 1 Hari", "1 Hari", atau "1H"). Sel kosong tidak mengubah data; angka 0 menghapus qty. Pelanggan yang belum ada akan dibuat otomatis.</p>
            : <p>Kolom opsional: Telepon, Alamat, Detail Alamat, Catatan. Nama yang sudah ada akan diperbarui; alamat baru otomatis ditambahkan ke Daftar Alamat.</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadCsv(`/import/template/${kind}`, isRecap ? `template-rekap-${period}` : "template-pelanggan", params, "xlsx")} data-testid="import-template-btn"><Download className="h-4 w-4 mr-2" />Unduh Template Excel</Button>
          <label className="inline-flex items-center h-9 px-3 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium cursor-pointer transition-colors">
            <Upload className="h-4 w-4 mr-2" />Pilih File
            <input type="file" accept=".xlsx,.xls,.xlsm,.csv" className="hidden" onChange={pick} data-testid="import-file-input" />
          </label>
          {file && <span className="text-sm self-center font-mono text-slate-600" data-testid="import-file-name">{file.name}</span>}
          {busy && <Loader2 className="h-4 w-4 animate-spin self-center" />}
        </div>
        {res && (
          <>
            <Summary kind={kind} res={res} />
            <div className="xl-wrap max-h-[45vh]" data-testid="import-preview">{isRecap ? <RecapPreview res={res} /> : <CustomerPreview res={res} />}</div>
            <div className="flex justify-end">
              <Button onClick={() => run(true)} disabled={busy || !okCount} className="bg-emerald-700 hover:bg-emerald-800" data-testid="import-commit-btn">Impor {okCount} Baris</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
